import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, API } from "@/lib/api";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { PRELACY_FILTERS } from "@/data/clergyFields";

const GROUP_FILTERS = ["Todos", ...PRELACY_FILTERS, "Bispos", "Presbíteros", "Diáconos"];

const SELECT =
  "bg-white border border-[#E5DEC9] px-3 py-2 text-xs uppercase tracking-widest text-[#1C1917] focus:outline-none focus:border-[#C5A059]";

function initials(nome) {
  return nome.split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("");
}

function matchGroup(c, group) {
  if (group === "Todos") return true;
  if (group === "Bispos") return /bispo|arcebispo/i.test(c.cargo || "");
  if (group === "Presbíteros") return /presb/i.test(c.cargo || "");
  if (group === "Diáconos") return /di[aá]cono/i.test(c.cargo || "");
  return c.prelazia === group;
}

function filterTestId(g) {
  return g.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

export default function ClergyDirectory() {
  const [clergy, setClergy] = useState([]);
  const [group, setGroup] = useState("Todos");
  const [uf, setUf] = useState("");
  const [cidade, setCidade] = useState("");

  useEffect(() => {
    api.get("/clergy").then((r) => setClergy(r.data)).catch(() => {});
  }, []);

  const ufs = useMemo(() => [...new Set(clergy.map((c) => c.uf).filter(Boolean))].sort(), [clergy]);
  const cidades = useMemo(
    () => [...new Set(clergy.filter((c) => !uf || c.uf === uf).map((c) => c.cidade).filter(Boolean))].sort(),
    [clergy, uf]
  );

  const filtered = clergy.filter(
    (c) => matchGroup(c, group) && (!uf || c.uf === uf) && (!cidade || c.cidade === cidade)
  );

  return (
    <div className="mt-16">
      <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
        <motion.div variants={fadeUp} data-testid="clergy-filter-bar" className="flex flex-wrap items-center gap-2">
          {GROUP_FILTERS.map((g) => (
            <button
              key={g}
              data-testid={`clergy-filter-${filterTestId(g)}`}
              onClick={() => setGroup(g)}
              className={`overline-label px-4 py-2 border transition-colors ${
                group === g
                  ? "bg-[#4A0E17] text-[#FBF9F4] border-[#4A0E17]"
                  : "border-[#E5DEC9] text-[#1C1917]/65 hover:border-[#4A0E17] hover:text-[#4A0E17]"
              }`}
            >
              {g}
            </button>
          ))}
          <select
            data-testid="clergy-filter-estado"
            value={uf}
            onChange={(e) => { setUf(e.target.value); setCidade(""); }}
            className={SELECT}
          >
            <option value="">Estado</option>
            {ufs.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
          <select
            data-testid="clergy-filter-cidade"
            value={cidade}
            onChange={(e) => setCidade(e.target.value)}
            className={SELECT}
          >
            <option value="">Cidade</option>
            {cidades.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </motion.div>

        <motion.p variants={fadeUp} data-testid="clergy-count" className="overline-label mt-6 text-[#4A0E17]/50">
          {filtered.length} {filtered.length === 1 ? "clérigo" : "clérigos"}
        </motion.p>

        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map((c, i) => (
            <motion.article
              key={c.id}
              variants={fadeUp}
              data-testid={`clergy-card-${i}`}
              className="group flex flex-col overflow-hidden border border-[#E5DEC9] bg-[#F4EFE6] transition-colors duration-500 hover:border-[#C5A059]"
            >
              <div className="overflow-hidden bg-[#2E070D]">
                {c.foto ? (
                  <img
                    src={`${API}/files/${c.foto}`}
                    alt={`Fotografia oficial de ${c.titulo} ${c.nome}`}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-4">
                    <span className="flex h-24 w-24 items-center justify-center rounded-full border border-[#C5A059]/40 bg-[#4A0E17] font-display text-3xl font-bold text-[#C5A059]">
                      {initials(c.nome)}
                    </span>
                    <span className="overline-label text-[#FBF9F4]/35">Fotografia em breve</span>
                  </div>
                )}
              </div>
              <div className="flex flex-1 flex-col p-6">
                <span className="overline-label text-[#C5A059]">{c.cargo}</span>
                <h3 className="font-display mt-2 text-lg font-bold leading-snug text-[#1C1917]">{c.titulo} {c.nome}</h3>
                {c.prelazia && <p className="mt-2 text-xs text-[#1C1917]/55">{c.prelazia}</p>}
                {c.comunidade && <p className="mt-1 text-xs leading-relaxed text-[#1C1917]/45">{c.comunidade}</p>}
                {(c.cidade || c.uf) && (
                  <p className="mt-3 text-sm font-semibold text-[#4A0E17]">{[c.cidade, c.uf].filter(Boolean).join(" — ")}</p>
                )}
                <Link
                  data-testid={`clergy-profile-link-${c.id}`}
                  to={`/clero/${c.slug || c.id}`}
                  className="overline-label mt-auto inline-flex items-center gap-2 self-start border-b border-[#C5A059] pt-5 pb-1 text-[#4A0E17] transition-colors hover:text-[#C5A059]"
                >
                  Ver perfil <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
        {filtered.length === 0 && (
          <p data-testid="clergy-empty" className="py-12 text-center text-sm text-[#1C1917]/50">
            Nenhum clérigo encontrado com os filtros selecionados.
          </p>
        )}
      </motion.div>
    </div>
  );
}
